/**
 * SSO Configuration Page
 *
 * Tenant admin page for managing SSO provider configurations.
 * Allows adding, editing, testing, and removing SSO providers.
 * Includes enforcement settings for requiring SSO login.
 */

import React, { useState, useCallback } from 'react';
import { DashboardLayout } from '../../../components/layout/DashboardLayout';
import { Card } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Badge } from '../../../components/ui/Badge';
import { Alert } from '../../../components/ui/Alert';
import { Spinner } from '../../../components/ui/Spinner';
import { useAuth } from '../../../contexts/AuthContext';
import { useLayoutProps } from '../../../hooks/useLayoutProps';
import { useToast } from '../../../hooks/useToast';
import { useConfirm } from '../../../hooks/useConfirm';
import { getErrorMessage } from '../../../lib/utils/errors';
import { SSOProviderType, SSOProviderStatus } from '../../../types/sso.types';
import type { SSOProviderConfig, SSOProviderCreateDTO, SSOProviderUpdateDTO } from '../../../types/sso.types';
import {
  useSSOProviders,
  useCreateSSOProvider,
  useUpdateSSOProvider,
  useDeleteSSOProvider,
  useTestSSOProvider,
} from '../hooks/useSSOProviders';
import { SSOProviderCard } from '../components/SSOProviderCard';
import { SSOProviderSetupWizard } from '../components/SSOProviderSetupWizard';
import { SSOProviderEditModal } from '../components/SSOProviderEditModal';

// ============================================================================
// COMPONENT
// ============================================================================

export const SSOConfigPage: React.FC = () => {
  const { tenant } = useAuth();
  const layoutProps = useLayoutProps();
  const toast = useToast();
  const { confirm, ConfirmDialog } = useConfirm();

  const tenantId = tenant?.tenantId ?? '';

  // Queries and mutations
  const { data: providers, isLoading, error: loadError } = useSSOProviders(tenantId || undefined);
  const createMutation = useCreateSSOProvider(tenantId);
  const updateMutation = useUpdateSSOProvider(tenantId);
  const deleteMutation = useDeleteSSOProvider(tenantId);
  const testMutation = useTestSSOProvider(tenantId);

  // Local state
  const [setupWizardType, setSetupWizardType] = useState<
    typeof SSOProviderType.MICROSOFT_ENTRA | typeof SSOProviderType.GOOGLE | null
  >(null);
  const [editingProvider, setEditingProvider] = useState<SSOProviderConfig | null>(null);
  const [editError, setEditError] = useState<string>('');

  // ==========================================================================
  // HANDLERS
  // ==========================================================================

  const handleSetup = (type: typeof SSOProviderType.MICROSOFT_ENTRA | typeof SSOProviderType.GOOGLE) => {
    setSetupWizardType(type);
  };

  const handleCreateProvider = useCallback(async (data: SSOProviderCreateDTO) => {
    try {
      await createMutation.mutateAsync(data);
      toast.success('SSO Provider Created', 'The SSO provider has been configured successfully.');
    } catch (err) {
      const message = getErrorMessage(err);
      toast.error('Failed to Create Provider', message);
      throw err;
    }
  }, [createMutation, toast]);

  const handleEditProvider = useCallback((provider: SSOProviderConfig) => {
    setEditingProvider(provider);
    setEditError('');
  }, []);

  const handleUpdateProvider = useCallback(async (providerId: string, data: SSOProviderUpdateDTO) => {
    try {
      await updateMutation.mutateAsync({ providerId, data });
      toast.success('Provider Updated', 'The SSO provider has been updated successfully.');
      setEditingProvider(null);
    } catch (err) {
      const message = getErrorMessage(err);
      setEditError(message);
      throw err;
    }
  }, [updateMutation, toast]);

  const handleToggleStatus = useCallback(async (provider: SSOProviderConfig) => {
    const isActive = provider.status === SSOProviderStatus.ACTIVE;
    const newStatus = isActive ? SSOProviderStatus.INACTIVE : SSOProviderStatus.ACTIVE;
    const action = isActive ? 'disable' : 'enable';

    const confirmed = await confirm({
      title: `${isActive ? 'Disable' : 'Enable'} SSO Provider`,
      message: `Are you sure you want to ${action} ${provider.displayName}? ${
        isActive
          ? 'Users will no longer be able to sign in with this provider.'
          : 'Users will be able to sign in with this provider.'
      }`,
      confirmText: isActive ? 'Disable' : 'Enable',
      variant: isActive ? 'danger' : 'primary',
    });

    if (!confirmed) return;

    try {
      await updateMutation.mutateAsync({
        providerId: provider.providerId,
        data: { status: newStatus },
      });
      toast.success(
        `Provider ${isActive ? 'Disabled' : 'Enabled'}`,
        `${provider.displayName} has been ${action}d.`
      );
    } catch (err) {
      toast.error('Update Failed', getErrorMessage(err));
    }
  }, [updateMutation, toast, confirm]);

  const handleDeleteProvider = useCallback(async (provider: SSOProviderConfig) => {
    const confirmed = await confirm({
      title: 'Delete SSO Provider',
      message: `Are you sure you want to delete ${provider.displayName}? This action cannot be undone. Users who sign in with this provider will no longer be able to use SSO.`,
      confirmText: 'Delete',
      variant: 'danger',
    });

    if (!confirmed) return;

    try {
      await deleteMutation.mutateAsync(provider.providerId);
      toast.success('Provider Deleted', `${provider.displayName} has been removed.`);
    } catch (err) {
      toast.error('Delete Failed', getErrorMessage(err));
    }
  }, [deleteMutation, toast, confirm]);

  const handleTestProvider = useCallback(async (provider: SSOProviderConfig) => {
    try {
      const result = await testMutation.mutateAsync(provider.providerId);
      if (result.success) {
        toast.success('Connection Test Passed', result.message);
      } else {
        toast.error('Connection Test Failed', result.message);
      }
    } catch (err) {
      toast.error('Test Failed', getErrorMessage(err));
    }
  }, [testMutation, toast]);

  const handleTestConnectionForWizard = useCallback(async (providerId: string) => {
    return await testMutation.mutateAsync(providerId);
  }, [testMutation]);

  // ==========================================================================
  // DERIVED STATE
  // ==========================================================================

  const isUpdating = updateMutation.isPending || deleteMutation.isPending;

  // ==========================================================================
  // RENDER
  // ==========================================================================

  if (!tenant) {
    return null;
  }

  return (
    <DashboardLayout {...layoutProps}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold" style={{ color: 'var(--color-text-primary)' }}>SSO Configuration</h1>
            <p className="mt-1 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
              Configure Single Sign-On providers for your organization
            </p>
          </div>
          <Badge variant="info" size="lg">Premium</Badge>
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <Spinner size="lg" label="Loading SSO configuration..." />
          </div>
        )}

        {/* Error State */}
        {loadError && (
          <Alert variant="error">
            Failed to load SSO configuration: {getErrorMessage(loadError)}
          </Alert>
        )}

        {/* Provider List */}
        {!isLoading && !loadError && (
          <>
            <Card>
              <Card.Header>
                <Card.Title>Identity Providers</Card.Title>
              </Card.Header>
              <Card.Body>
                <div className="space-y-4">
                  {/* Configured providers */}
                  {providers && providers.length > 0 && (
                    <div className="space-y-3">
                      {providers.map((provider) => (
                        <SSOProviderCard
                          key={provider.providerId}
                          provider={provider}
                          onEdit={handleEditProvider}
                          onToggleStatus={handleToggleStatus}
                          onDelete={handleDeleteProvider}
                          onTest={handleTestProvider}
                          isUpdating={isUpdating}
                        />
                      ))}
                    </div>
                  )}

                  {/* Add Provider Buttons */}
                  <div className="pt-4 border-t" style={{ borderColor: 'var(--color-border)' }}>
                    <p className="text-sm font-medium mb-3" style={{ color: 'var(--color-text-primary)' }}>
                      Add a new identity provider
                    </p>
                    <div className="flex gap-3">
                      <Button
                        variant="secondary"
                        onClick={() => handleSetup(SSOProviderType.MICROSOFT_ENTRA)}
                        leftIcon={
                          <svg className="h-4 w-4" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <rect x="1" y="1" width="9" height="9" fill="#F25022" />
                            <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
                            <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
                            <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
                          </svg>
                        }
                      >
                        Add Microsoft Entra ID
                      </Button>
                      <Button
                        variant="secondary"
                        onClick={() => handleSetup(SSOProviderType.GOOGLE)}
                        leftIcon={
                          <svg className="h-4 w-4" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
                            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                          </svg>
                        }
                      >
                        Add Google
                      </Button>
                    </div>
                  </div>

                  {/* Empty state */}
                  {(!providers || providers.length === 0) && (
                    <div className="text-center py-6">
                      <p className="text-sm mb-2" style={{ color: 'var(--color-text-secondary)' }}>
                        No SSO providers configured yet. Add a provider to enable Single Sign-On for your organization.
                      </p>
                    </div>
                  )}
                </div>
              </Card.Body>
            </Card>

            {/* Settings Card */}
            <Card>
              <Card.Header>
                <Card.Title>Settings</Card.Title>
              </Card.Header>
              <Card.Body>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <dt className="text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>Active Providers</dt>
                      <dd className="mt-1 text-sm" style={{ color: 'var(--color-text-primary)' }}>
                        {providers?.filter((p) => p.status === SSOProviderStatus.ACTIVE).length || 0} of{' '}
                        {providers?.length || 0}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>SSO Enforcement</dt>
                      <dd className="mt-1 text-sm" style={{ color: 'var(--color-text-primary)' }}>
                        {providers?.some((p) => p.enforceSSO && p.status === SSOProviderStatus.ACTIVE)
                          ? <Badge variant="warning" size="sm">Enforced — password login disabled</Badge>
                          : <Badge variant="neutral" size="sm">Not enforced</Badge>
                        }
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>Auto-Provisioning</dt>
                      <dd className="mt-1 text-sm" style={{ color: 'var(--color-text-primary)' }}>
                        {providers?.some((p) => p.autoProvisionUsers && p.status === SSOProviderStatus.ACTIVE)
                          ? <Badge variant="info" size="sm">Enabled</Badge>
                          : <Badge variant="neutral" size="sm">Disabled</Badge>
                        }
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>Callback URL</dt>
                      <dd className="mt-1 text-xs font-mono px-2 py-1 rounded break-all" style={{ color: 'var(--color-text-primary)', backgroundColor: 'var(--color-bg-sunken)' }}>
                        {window.location.origin}/auth/sso/callback
                      </dd>
                    </div>
                  </div>

                  <Alert variant="info">
                    SSO enforcement and auto-provisioning can be configured per-provider using the "Edit" button on each provider above.
                  </Alert>
                </div>
              </Card.Body>
            </Card>
          </>
        )}
      </div>

      {/* Setup Wizard */}
      {setupWizardType && (
        <SSOProviderSetupWizard
          isOpen={true}
          onClose={() => setSetupWizardType(null)}
          providerType={setupWizardType}
          onSubmit={handleCreateProvider}
          onTestConnection={handleTestConnectionForWizard}
          isSubmitting={createMutation.isPending}
        />
      )}

      {/* Edit Modal */}
      <SSOProviderEditModal
        isOpen={!!editingProvider}
        onClose={() => {
          setEditingProvider(null);
          setEditError('');
        }}
        provider={editingProvider}
        onSubmit={handleUpdateProvider}
        isSubmitting={updateMutation.isPending}
        error={editError}
      />

      {/* Confirm Dialog */}
      {ConfirmDialog}
    </DashboardLayout>
  );
};

export default SSOConfigPage;
